import React, { useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@cotera/client/v0/components/ui/hover-card';
import { cn } from '@cotera/client/v0/lib/utils';
import { RESOURCE_TYPE_CONFIG } from '@cotera/client/app/v1/resources/type/resource-type-config';
import { Favicon } from '../tool-renderers/web-favicon';
import { useCitations } from './citation-context';
import {
  parseCitationGroup,
  type CitationGroupEntry,
} from './rehype-group-citations';
import type { CitationSource } from './citation-source';

/** Favicons shown before collapsing into a `+N` counter. */
const MAX_STACKED_FAVICONS = 3;

const SourceIcon: React.FC<{ source: CitationSource; className?: string }> = ({
  source,
  className,
}) => {
  if (source.kind === 'web') {
    return <Favicon url={source.url} className={className} />;
  }
  const config = RESOURCE_TYPE_CONFIG[source.resourceType];
  const Icon = config?.icon;
  if (Icon === undefined) {
    return null;
  }
  return (
    <Icon className={cn('text-muted-foreground', className)} aria-hidden />
  );
};

/** A resolved citation plus the href the model actually wrote for it. */
type CitationItem = { source: CitationSource; href: string };

/** Secondary line in the hover card: host for web, resource kind for in-app. */
function subLabel(source: CitationSource): string {
  if (source.kind === 'web') {
    return source.siteLabel;
  }
  return RESOURCE_TYPE_CONFIG[source.resourceType]?.label ?? 'Workspace';
}

/**
 * One chip standing for a run of adjacent citations, styled after
 * `LoadedToolIcons`: overlapping icons then a `+N` overflow counter.
 */
const CitationChipGroup: React.FC<{ items: CitationItem[] }> = ({ items }) => {
  const { onCitationClick } = useCitations();
  const [primary] = items;

  // One favicon request per host, not per chip.
  const uniqueBySite = useMemo(() => {
    const seen = new Set<string>();
    return items.filter(({ source }) => {
      if (seen.has(source.siteLabel)) {
        return false;
      }
      seen.add(source.siteLabel);
      return true;
    });
  }, [items]);

  if (primary === undefined) {
    return null;
  }

  const visible = uniqueBySite.slice(0, MAX_STACKED_FAVICONS);
  const overflow = items.length - 1;

  const handleClick =
    (item: CitationItem) =>
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      onCitationClick?.(item.source);
      // Resource citations are relative hrefs, which rehype-harden stamps with
      // `target="_blank"` — following them would leave the SPA. The host routes
      // them to the artifact panel instead.
      if (item.source.kind === 'resource') {
        event.preventDefault();
      }
    };

  return (
    <HoverCard closeDelay={100} openDelay={150}>
      <HoverCardTrigger asChild>
        <Badge
          asChild
          variant="muted"
          size="sm"
          className="ml-1 rounded-full align-baseline select-none cursor-pointer hover:bg-accent"
        >
          <a
            href={primary.href}
            onClick={handleClick(primary)}
            target={primary.source.kind === 'web' ? '_blank' : undefined}
            rel={
              primary.source.kind === 'web' ? 'noopener noreferrer' : undefined
            }
            aria-label={
              items.length === 1
                ? `Source: ${primary.source.siteLabel}`
                : `Sources: ${items
                    .map((item) => item.source.siteLabel)
                    .join(', ')}`
            }
          >
            <span className="flex items-center -space-x-1">
              {visible.map((item, index) => (
                <span
                  key={`${item.source.siteLabel}-${index}`}
                  className="flex h-3.5 w-3.5 items-center justify-center overflow-hidden rounded-full bg-background"
                  style={{ zIndex: visible.length - index }}
                >
                  <SourceIcon source={item.source} className="h-3 w-3" />
                </span>
              ))}
            </span>
            <span className="max-w-40 truncate">
              {primary.source.siteLabel}
            </span>
            {overflow > 0 && (
              <span className="text-muted-foreground">+{overflow}</span>
            )}
          </a>
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 p-0">
        <ul className="divide-y divide-border">
          {items.map((item, index) => (
            <li key={`${item.href}-${index}`}>
              <a
                href={item.href}
                target={item.source.kind === 'web' ? '_blank' : undefined}
                rel={
                  item.source.kind === 'web' ? 'noopener noreferrer' : undefined
                }
                onClick={handleClick(item)}
                className="flex items-start gap-2 p-3 hover:bg-accent"
              >
                <SourceIcon
                  source={item.source}
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {item.source.title ?? item.source.siteLabel}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {subLabel(item.source)}
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
};

type AnchorProps = React.ComponentProps<'a'> & {
  /** Written by `rehypeGroupCitations`; JSON `{href,label}[]`. */
  'data-cite-group'?: string;
};

/**
 * An in-app resource deep link that Coco writes for e.g. similar-resource
 * suggestions: `/<org-slug>/<segment>/<uuid>`, like `/acme/agent/<uuid>`. Scoped
 * to the resource-page shape (three segments ending in a UUID, with an optional
 * query/hash) rather than any relative href, so ordinary relative links in prose
 * stay plain anchors and only real resource links get the open-in-new-tab
 * affordance. Distinct from web links and from citation chips (which load into
 * the artifact panel).
 */
const IN_APP_RESOURCE_HREF =
  /^\/[^/]+\/[^/]+\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:[?#].*)?$/i;

function isInAppResourceHref(href: string | undefined): href is string {
  return href !== undefined && IN_APP_RESOURCE_HREF.test(href);
}

/**
 * The look of an ordinary prose link. Our `a` override replaces Streamdown's
 * styled anchor, so without this links render identical to body text. In the
 * v1 theme `text-primary` sits close to the foreground navy, so the underline —
 * not the colour — is what reads as "link"; it firms up on hover.
 */
const PROSE_LINK_CLASS =
  'wrap-anywhere font-medium text-primary underline decoration-primary/40 underline-offset-[3px] transition-colors hover:decoration-primary';

const EXTERNAL_HREF = /^https?:\/\//i;

/**
 * An ordinary (non-citation, non-resource) link. Absolute web links open in a
 * new tab like Streamdown's own anchor did, so following one never navigates
 * away from the chat; relative links keep normal navigation.
 */
const ProseLink: React.FC<AnchorProps> = ({ className, href, ...rest }) => {
  const external = href !== undefined && EXTERNAL_HREF.test(href);
  return (
    //eslint-disable-next-line jsx-a11y/anchor-has-content
    <a
      {...rest}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={cn(PROSE_LINK_CLASS, className)}
    />
  );
};

/**
 * A plain navigating link to an in-app resource page, given a subtle dashed
 * underline and a trailing arrow so it reads as "opens elsewhere" without the
 * heavier look of a citation chip. It never loads the resource into the chat.
 */
const InAppOpenLink: React.FC<AnchorProps> = ({
  children,
  className,
  ...rest
}) => (
  //eslint-disable-next-line jsx-a11y/anchor-has-content
  <a
    {...rest}
    target="_blank"
    rel="noopener noreferrer"
    className={cn('group/open inline no-underline', className)}
  >
    <span className="underline decoration-dashed decoration-muted-foreground/40 underline-offset-2 group-hover/open:decoration-muted-foreground">
      {children}
    </span>
    <ArrowUpRight
      className="ml-0.5 inline-block h-3.5 w-3.5 -translate-y-px text-muted-foreground group-hover/open:text-foreground"
      aria-hidden={true}
    />
  </a>
);

/**
 * The single markdown component override citations need.
 *
 * A run only becomes a chip when *every* entry resolves to a known source and
 * the link text is the site label. Both conditions matter: the first rejects
 * URLs the model invented, the second keeps ordinary descriptive prose links
 * (`the [YC application page](…)`) as links even when that host was scraped.
 */
export const CitationAnchor: React.FC<AnchorProps> = ({
  'data-cite-group': citeGroup,
  ...props
}) => {
  const { resolve } = useCitations();

  const entries = useMemo<CitationGroupEntry[]>(() => {
    const parsed = parseCitationGroup(citeGroup);
    if (parsed !== undefined) {
      return parsed;
    }
    return typeof props.href === 'string' && props.href !== ''
      ? [{ href: props.href, label: '' }]
      : [];
  }, [citeGroup, props.href]);

  const items = useMemo(() => {
    const resolved: CitationItem[] = [];
    for (const entry of entries) {
      const source = resolve(entry.href);
      if (source === null || entry.label !== source.siteLabel) {
        return null;
      }
      resolved.push({ source, href: entry.href });
    }
    return resolved.length > 0 ? resolved : null;
  }, [entries, resolve]);

  if (items !== null) {
    return <CitationChipGroup items={items} />;
  }

  // Not a citation — restore the run exactly as authored as prose links, but
  // give in-app resource links (e.g. Coco's "Open <title>" suggestions) a
  // dashed-underline + arrow affordance. This is why the rehype plugin carries `{href,label}` pairs
  // rather than bare hrefs.
  if (entries.length > 1) {
    return (
      <>
        {entries.map((entry, index) =>
          isInAppResourceHref(entry.href) ? (
            <InAppOpenLink
              key={`${entry.href}-${index}`}
              {...props}
              href={entry.href}
              children={entry.label}
            />
          ) : (
            <ProseLink
              key={`${entry.href}-${index}`}
              {...props}
              href={entry.href}
              children={entry.label}
            />
          )
        )}
      </>
    );
  }

  if (isInAppResourceHref(props.href)) {
    return <InAppOpenLink {...props} />;
  }

  return <ProseLink {...props} />;
};
